import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { b2bApi, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { Stat, StatusPill, Card, fmtDateTime } from "@/components/common/Stat";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Users2, ShieldCheck, Clock, Lock } from "lucide-react";

const useClients = () => useQuery({ queryKey: ["oh-clients"], queryFn: async () => (await b2bApi.get("/b2b/oh/clients")).data, retry: false });

export function OhHome() {
  const t = useT();
  const me = useQuery({ queryKey: ["oh-me"], queryFn: async () => (await b2bApi.get("/b2b/oh/me")).data, retry: false });
  const s = me.data?.stats || {};
  return (
    <div className="space-y-8">
      <PageHeader eyebrow={t("b2b.nav.oh")} title={t("b2b.oh.title")} subtitle={t("b2b.oh.subtitle")} />
      <div className="grid grid-cols-3 gap-3">
        <Stat icon={Users2} label={t("b2b.oh.clients")} value={s.clients ?? 0} testId="oh-stat-clients" />
        <Stat icon={ShieldCheck} label={t("b2b.oh.active")} value={s.active ?? 0} testId="oh-stat-active" />
        <Stat icon={Clock} label={t("b2b.oh.pending")} value={s.pending ?? 0} testId="oh-stat-pending" />
      </div>
      <Card title={t("b2b.consent.whyTitle")} testId="oh-privacy-card">
        <div className="flex items-start gap-3 text-sm text-ink-700"><Lock className="h-5 w-5 text-sage-700 shrink-0 mt-0.5" strokeWidth={1.5} /><p>{t("b2b.oh.inviteBody")}</p></div>
        <Link to="/oh/clients" className="inline-block mt-4 text-sm text-sage-600 font-semibold" data-testid="oh-goto-clients">{t("b2b.oh.clients")} →</Link>
      </Card>
    </div>
  );
}

export function OhClients() {
  const t = useT();
  const qc = useQueryClient();
  const list = useClients();
  const [email, setEmail] = useState(""); const [purpose, setPurpose] = useState("verzuimbegeleiding"); const [message, setMessage] = useState(""); const [busy, setBusy] = useState(false);
  const invite = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await b2bApi.post("/b2b/oh/clients/invite", { email: email.trim(), purpose, message: message || null }); toast.success(t("common.saved")); setEmail(""); setMessage(""); qc.invalidateQueries({ queryKey: ["oh-clients"] }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const clients = list.data?.clients || [];
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("b2b.nav.oh")} title={t("b2b.oh.clients")} subtitle={t("b2b.oh.inviteBody")} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={t("b2b.oh.invite")} testId="oh-invite-card">
          <form onSubmit={invite} className="space-y-3" data-testid="oh-invite-form">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="medewerker@bedrijf.nl" className="h-11 rounded-xl bg-white" data-testid="oh-invite-email" />
            <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder={t("b2b.oh.purpose")} className="h-11 rounded-xl bg-white" data-testid="oh-invite-purpose" />
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("b2b.oh.message")} maxLength={280} className="rounded-xl bg-white" data-testid="oh-invite-message" />
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="oh-invite-submit">{t("b2b.oh.invite")}</Button>
          </form>
        </Card>
        <Card title={t("b2b.oh.clients")} testId="oh-client-list" className="lg:col-span-2">
          <div className="divide-y divide-border">
            {clients.map((c) => (
              <Link key={c.id} to={`/oh/clients/${c.id}`} className="py-3 flex items-center justify-between gap-3 text-sm hover:bg-cream-50 rounded-lg px-1" data-testid={`oh-client-${c.id}`}>
                <div className="min-w-0"><div className="text-ink-900 font-medium truncate">{c.employee_email}</div><div className="text-xs text-ink-500">{c.shared_categories.length} {t("b2b.consent.categories").toLowerCase()} · {fmtDateTime(c.created_at)}</div></div>
                <StatusPill status={c.status} label={t(`b2b.oh.statuses.${c.status}`)} />
              </Link>
            ))}
            {!clients.length && <div className="text-sm text-ink-500 py-3">{list.isLoading ? t("common.loading") : t("b2b.oh.noClients")}</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function OhClientDetail() {
  const t = useT();
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["oh-client", id], queryFn: async () => (await b2bApi.get(`/b2b/oh/clients/${id}`)).data, retry: false });
  const withdraw = async () => {
    try { await b2bApi.post(`/b2b/oh/clients/${id}/withdraw`); toast.success(t("common.saved")); qc.invalidateQueries({ queryKey: ["oh-client", id] }); qc.invalidateQueries({ queryKey: ["oh-clients"] }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const d = q.data;
  if (q.isLoading) return <div className="text-sm text-ink-500">{t("common.loading")}</div>;
  if (q.isError) return <div className="text-sm text-rust-600">{errMsg(q.error)}</div>;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("b2b.oh.detail")} title={d.employee_email} subtitle={`${t("b2b.oh.purpose")}: ${d.purpose || "—"}`}
        actions={<div className="flex items-center gap-2"><StatusPill status={d.status} label={t(`b2b.oh.statuses.${d.status}`)} testId="oh-client-status" />
          {["pending", "active"].includes(d.status) && <Button variant="outline" className="rounded-full" onClick={withdraw} data-testid="oh-client-withdraw">{t("b2b.oh.withdraw")}</Button>}</div>} />
      <Card title={t("b2b.oh.shared")} testId="oh-client-shared">
        {d.access === "granted" ? (
          <>
            <div className="flex flex-wrap gap-2">
              {d.shared_categories.map((c) => <span key={c} className="rounded-full bg-sage-100 text-sage-700 px-3 py-1 text-xs font-semibold" data-testid={`oh-shared-${c}`}>{t(`b2b.consent.cat.${c}`)}</span>)}
            </div>
            <p className="text-xs text-ink-500 mt-4">{t("b2b.oh.dataNote")}</p>
          </>
        ) : (
          <div className="flex items-start gap-3 text-sm text-ink-700" data-testid="oh-nothing-shared"><Lock className="h-5 w-5 text-ink-500 shrink-0 mt-0.5" strokeWidth={1.5} />{t("b2b.oh.nothingShared")}</div>
        )}
      </Card>
    </div>
  );
}
